import { ArrowDown, CheckCircle, Warning } from '@phosphor-icons/react';
import { SKIP_REASON_LABEL } from '../utils/analytics-labels';
import { fmtNum } from '../utils/report-format';
import './campaign-report-funnel.css';

type Stage = {
  label: string;
  v: number;
  lossWord: string | null;
};

type CampaignReportFunnelProps = {
  audienceTotal: number;
  sendable: number;
  sent: number;
  delivered: number;
  uniqueOpens: number;
  uniqueClicks: number;
  skipped: number;
  skips: Record<string, number>;
  balances: boolean | null;
};

export function CampaignReportFunnel({
  audienceTotal,
  sendable,
  sent,
  delivered,
  uniqueOpens,
  uniqueClicks,
  skipped,
  skips,
  balances,
}: CampaignReportFunnelProps) {
  const stages: Stage[] = [
    { label: 'Audience', v: audienceTotal, lossWord: null },
    { label: 'Sendable', v: sendable, lossWord: 'skipped' },
    { label: 'Sent', v: sent, lossWord: 'not sent' },
    { label: 'Delivered', v: delivered, lossWord: 'bounced' },
    { label: 'Opened', v: uniqueOpens, lossWord: 'never opened' },
    { label: 'Clicked', v: uniqueClicks, lossWord: 'never clicked' },
  ];
  const max = Math.max(1, audienceTotal);

  const VW = 600;
  const VH = 96;
  const H = 78;
  const cy = VH / 2;
  const xAt = (i: number) => (i + 0.5) * (VW / stages.length);
  const hAt = (v: number) => Math.max(3, (v / max) * H);

  const top: string[] = [`0,${cy - hAt(stages[0].v) / 2}`];
  const bottom: string[] = [`0,${cy + hAt(stages[0].v) / 2}`];
  stages.forEach((s, i) => {
    const h = hAt(s.v);
    top.push(`${xAt(i)},${cy - h / 2}`);
    bottom.push(`${xAt(i)},${cy + h / 2}`);
  });
  const last = hAt(stages[stages.length - 1].v);
  top.push(`${VW},${cy - last / 2}`);
  bottom.push(`${VW},${cy + last / 2}`);
  const polygon = `${top.join(' ')} ${[...bottom].reverse().join(' ')}`;

  const reasonKeys = Object.keys(skips).filter((k) => skips[k]);
  const skipLine = !skipped ? (
    <span className="text-neutral-600">
      No recipients were skipped — everyone in the audience was sendable.
    </span>
  ) : (
    <span>
      <strong className="font-semibold text-neutral-900">
        {fmtNum(skipped)} skipped before send
      </strong>
      <span className="text-neutral-600">
        {' '}
        —{' '}
        {reasonKeys
          .map((k) => `${fmtNum(skips[k])} ${(SKIP_REASON_LABEL[k] || k).toLowerCase()}`)
          .join(' · ')}
      </span>
    </span>
  );

  const endToEnd = audienceTotal
    ? `${fmtNum(uniqueClicks)} of ${fmtNum(audienceTotal)} clicked`
    : '';

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-5 shadow-sm">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold text-neutral-900">Funnel</h3>
          <p className="text-xs text-neutral-500">Where recipients dropped out, stage by stage.</p>
        </div>
        {endToEnd ? <p className="text-xs tabular-nums text-neutral-600">{endToEnd}</p> : null}
      </div>

      <div className="report-funnel-wide">
        <div className="report-funnel-grid">
          {stages.map((s) => (
            <div key={`n-${s.label}`} className="report-funnel-col">
              <span className="report-funnel-num">{fmtNum(s.v)}</span>
            </div>
          ))}
        </div>
        <div className="report-funnel-ribbon">
          <svg
            viewBox={`0 0 ${VW} ${VH}`}
            width="100%"
            height={VH}
            preserveAspectRatio="none"
            role="img"
            aria-label={`Funnel from ${fmtNum(audienceTotal)} in the audience to ${fmtNum(uniqueClicks)} unique clicks`}
          >
            <polygon
              points={polygon}
              fill="#9ec5f4"
              stroke="#2a78d6"
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
            {stages.map((s, i) => {
              const h = hAt(s.v);
              return (
                <line
                  key={s.label}
                  x1={xAt(i)}
                  x2={xAt(i)}
                  y1={cy - h / 2}
                  y2={cy + h / 2}
                  stroke="#ffffff"
                  strokeWidth="1.5"
                  strokeOpacity=".65"
                />
              );
            })}
          </svg>
        </div>
        <div className="report-funnel-grid mt-2">
          {stages.map((s) => (
            <div key={`l-${s.label}`} className="report-funnel-col">
              <span className="report-funnel-label">{s.label}</span>
            </div>
          ))}
        </div>
        <div className="report-funnel-grid">
          {stages.map((s, i) => {
            if (i === 0 || !s.lossWord) {
              return <div key={`loss-${s.label}`} className="report-funnel-col" />;
            }
            const lost = Math.max(0, stages[i - 1].v - s.v);
            return (
              <div key={`loss-${s.label}`} className="report-funnel-col">
                {lost ? (
                  <span className="report-funnel-loss">
                    <ArrowDown className="size-[11px]" aria-hidden />
                    {fmtNum(lost)} {s.lossWord}
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      <div className="report-funnel-narrow">
        {stages.map((s, i) => {
          const lost = i === 0 ? 0 : Math.max(0, stages[i - 1].v - s.v);
          const bar = Math.max(2, Math.round((s.v / max) * 100));
          return (
            <div key={`nr-${s.label}`} className="report-funnel-nrow">
              <span className="report-funnel-nlabel">{s.label}</span>
              <span className="report-funnel-nloss">
                {lost && s.lossWord ? `${fmtNum(lost)} ${s.lossWord}` : ''}
              </span>
              <span className="report-funnel-nval">{fmtNum(s.v)}</span>
              <span className="report-funnel-nbar" style={{ width: `${bar}%` }} />
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-4 border-t border-neutral-100 pt-3 text-xs">
        <span>{skipLine}</span>
        {balances === null ? null : balances ? (
          <span className="inline-flex items-center gap-1.5 text-neutral-500">
            <CheckCircle className="size-[13px] text-[#0A7A3E]" aria-hidden />
            Sent + skipped = audience
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-[#B4690E]">
            <Warning className="size-[13px]" aria-hidden />
            Sent + skipped does not equal audience — the rollup may be stale
          </span>
        )}
      </div>
    </div>
  );
}
